<template>
    <ExpansionPanel
        v-if="isWidgetVisible"
        :label="$t('mechanic.Identify Component')"
        icon="fa-regular fa-gear"
        data-qs="identify-part-widget"
    >
        <div class="row" :class="isXSmall ? 'q-col-gutter-sm' : 'q-col-gutter-lg'">
            <div class="col-12 col-md-6">
                <IdentifyPartItem
                    :product-id="productId ?? null"
                    :path-to-root-assembly="pathToRootAssembly ?? null"
                    :has-sub-assemblies="hasSubAssemblies"
                    @open-assembly-navigation="showOverlay"
                />
            </div>
            <ShowIfHasAccess :feature-id="AccessFeature.parts_classification_search_gui">
                <div class="col-12 col-md-6">
                    <ClassificationSearch :product="productId ?? ''" :asset="assetId ?? ''" />
                </div>
            </ShowIfHasAccess>
            <!-- No entrance in combined view for mobile devices-->
            <ShowIfHasAccess v-if="!isXSmall" :feature-id="AccessFeature.combined_view">
                <div class="col-12 col-md-6">
                    <EnterCombinedView />
                </div>
            </ShowIfHasAccess>
        </div>
    </ExpansionPanel>
    <TitleContentFooterDialog
        v-model="overlayVisible"
        test-id-prefix="identify-part-widget"
        position="left"
        :title="$t('mechanic.Select Assembly')"
        icon="fa-light fa-sitemap icon-light-color"
    >
        <AssemblyNav :asset="assetId" :product="productId ?? ''" @hide-overlay="hideOverlay"> </AssemblyNav>
    </TitleContentFooterDialog>
</template>

<script setup lang="ts">
import IdentifyPartItem from "@/abilities/mechanic/pages/components/identify-part-item.vue";
import ClassificationSearch from "@/abilities/mechanic/pages/widgets/classification-search.vue";
import EnterCombinedView from "@/abilities/mechanic/pages/widgets/enter-combined-view.vue";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import ShowIfHasAccess from "@/shared/access-control/components/show-if-has-access.vue";
import TitleContentFooterDialog from "@/shared/components/side-panel/dialog/title-content-footer-dialog.vue";
import ExpansionPanel from "@/shared/components/expansion/expansion-panel.vue";
import { WidgetContext } from "@/shared/environment/widgets/widget-context";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import {
    AvailablePartsListDocument,
    useAvailablePartsListLazyQuery,
    useAvailablePartsListQuery,
} from "@/shared/services/graphql/generated/consumer-graph-types";
import { useNotificationStore } from "@/shared/store/notification-store";
import { computed, ref, toRefs, watch } from "vue";
import AssemblyNav from "../components/assembly-navigation.vue";

const props = defineProps<{
    widgetContext: WidgetContext;
}>();

const { widgetContext } = toRefs(props);

const notificationStore = useNotificationStore();

const { isXSmall } = useScreenSize();

watch(
    () => notificationStore.mechanicDeleted,
    (value) => {
        if (value > 0) {
            refetch();
        }
    }
);

const overlayVisible = ref(false);

function hideOverlay() {
    overlayVisible.value = false;
}

function showOverlay() {
    overlayVisible.value = true;
}

//one and only assembly of root "" has subassemblies
const hasSubAssemblies = ref(true);
// path to one and only assembly of root ""
const pathToRootAssembly = ref<string[] | undefined>(undefined);

const productId = computed(() => {
    if (widgetContext.value.__type === "product") {
        return widgetContext.value.productId;
    }
    if (widgetContext.value.__type === "asset") {
        return widgetContext.value.product;
    }
    return undefined;
});

const assetId = computed(() => {
    if (widgetContext.value.__type === "asset") {
        return widgetContext.value.asset;
    }
    return undefined;
});

const filterVariables = computed(() => {
    return {
        filter: {
            andGroup: [
                {
                    equals: {
                        parentAssemblyId: "",
                    },
                },
            ],
        },
        assetId: assetId.value,
        productId: productId.value,
        first: 2,
    };
});

const { loading, result, refetch, onResult } = useAvailablePartsListQuery(
    filterVariables.value,
    computed(() => ({
        fetchPolicy: "no-cache",
        enabled: productId.value !== undefined,
    }))
);

const { load: loadHasSubAssemblies, onResult: onHasSubAssembliesResult } = useAvailablePartsListLazyQuery(
    filterVariables.value
);

onResult(({ data }) => {
    //if there is exactly one assembly, check if it has subassemblies
    if (data.mechanics.mechanics.length === 1 && data.mechanics.mechanics[0]?.node.assemblyId) {
        loadHasSubAssemblies(AvailablePartsListDocument, {
            filter: {
                andGroup: [
                    {
                        equals: {
                            parentAssemblyId: data.mechanics.mechanics[0].node.assemblyId,
                        },
                    },
                    {
                        equals: {
                            isAssembly: true,
                        },
                    },
                ],
            },
            assetId: assetId.value,
            productId: productId.value,
            first: 1,
        });
        if (data.mechanics.mechanics[0]?.node?.assemblyId) {
            pathToRootAssembly.value = ["", data.mechanics.mechanics[0]?.node?.assemblyId];
        }
    }
});

onHasSubAssembliesResult(({ data }) => {
    if (data.mechanics.mechanics && data.mechanics.mechanics.length === 0) {
        hasSubAssemblies.value = false;
    }
});

const isWidgetVisible = computed(() => {
    return (
        result.value?.mechanics !== undefined &&
        !loading.value &&
        result.value.mechanics.mechanics &&
        result.value.mechanics.mechanics.length > 0
    );
});
</script>
